<template>
  <div class="w-full">
    <div class="mb-4">
      <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div class="flex items-center gap-2 text-sm text-gray-500 md:ml-auto justify-end w-full md:w-auto flex-wrap">
          <span class="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 border border-gray-200">
            <b-icon pack="fas" icon="cube" size="is-small"></b-icon>
            <span class="ml-2">
              {{ models.length }} {{ $t('individual_models') || 'Individuales' }}
            </span>
          </span>

          <span class="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 border border-gray-200">
            <b-icon pack="fas" icon="layer-group" size="is-small"></b-icon>
            <span class="ml-2">
              {{ federatedGroups.length }} {{ $t('federated_models') || 'Federados' }}
            </span>
          </span>
        </div>
      </div>
    </div>

    <div class="mb-4">
      <div class="inline-flex bg-gray-100 rounded-xl p-1 border border-gray-200 flex-wrap">
        <button
          type="button"
          @click="activeSection = 'individual'"
          class="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 flex items-center"
          :class="activeSection === 'individual'
            ? 'bg-white text-blue-600 shadow-sm border border-gray-200'
            : 'text-gray-600 hover:text-gray-800'"
        >
          <b-icon pack="fas" icon="cube" size="is-small"></b-icon>
          <span class="ml-2">{{ $t('individual_models') || 'Modelos Individuales' }}</span>
        </button>

        <button
          type="button"
          @click="activeSection = 'federated'"
          class="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 flex items-center"
          :class="activeSection === 'federated'
            ? 'bg-white text-blue-600 shadow-sm border border-gray-200'
            : 'text-gray-600 hover:text-gray-800'"
        >
          <b-icon pack="fas" icon="layer-group" size="is-small"></b-icon>
          <span class="ml-2">{{ $t('federated_models') || 'Modelos Federados' }}</span>
        </button>
      </div>
    </div>

    <div v-if="activeSection === 'individual'" class="panel ifc-panel">
      <div class="panel-heading ifc-panel-heading">
        <div class="flex items-center justify-between w-full gap-4 flex-wrap">
          <div class="min-w-0">
            <div class="flex items-center text-gray-800">
              <b-icon pack="fas" icon="cube" class="mr-2" />
              <span class="font-semibold text-xl">
                {{ $t('individual_models') || 'Modelos Individuales' }}
              </span>
            </div>

            <p class="text-xs text-gray-500 mt-1">
              {{ models.length }} {{ $t('file') }}
              ·
              {{ individualReadyCount }} {{ $t('ready') }}
            </p>
          </div>

          <button class="button is-primary is-small" @click="$emit('open-individual-upload')">
            <b-icon pack="fas" icon="plus" size="is-small" />&nbsp;
            {{ $t('add_ifc') }}
          </button>
        </div>
      </div>

      <div v-if="isLoading" class="my-4 text-center">
        <scale-loader></scale-loader>
      </div>

      <div v-else>
        <div class="flex flex-wrap w-full mt-4">
          <section class="w-full">
            <b-table v-if="!isLoading" icon-pack="fas" :data="models" :bordered="true">
              <template>
                <b-table-column field="name" cell-class="vertical-center" sortable>
                  <template v-slot:header>
                    {{ $t('name') }}
                  </template>

                  <template v-slot="props">
                    <div class="flex items-center min-w-0">
                      <b-icon pack="fas" icon="cube" class="text-gray-400 mr-2 flex-shrink-0" />
                      <span class="truncate" :title="props.row.name">
                        {{ props.row.name }}
                      </span>
                    </div>
                  </template>
                </b-table-column>

                <b-table-column field="loaded" cell-class="vertical-center" width="160">
                  <template v-slot:header>
                    {{ $t('status') || 'Estado' }}
                  </template>

                  <template v-slot="props">
                    <div class="flex items-center justify-center">
                      <template v-if="props.row.xktpath">
                        <b-icon
                          pack="fas"
                          icon="check"
                          class="text-green"
                        />
                        <span class="ml-2">{{ $t('ready') }}</span>
                      </template>

                      <template v-else>
                        <b-icon
                          pack="fas"
                          icon="times"
                          class="text-red"
                        />
                        <span class="ml-2">{{ $t('pending') }}</span>
                      </template>
                    </div>
                  </template>
                </b-table-column>

                <b-table-column field="actions" cell-class="vertical-center" width="100">
                  <template v-slot:header>
                    {{ $t('actions') || 'Acciones' }}
                  </template>

                  <template v-slot="props">
                    <div class="flex items-center justify-around w-full h-full">
                      <b-tooltip :label="$t('delete')" position="is-left" type="is-danger">
                        <div @click="removeIfc(props.row)" class="flex cursor-pointer text-red">
                          <b-icon pack="fas" icon="trash-alt" />
                        </div>
                      </b-tooltip>
                    </div>
                  </template>
                </b-table-column>
              </template>

              <template slot="bottom-left">
                <b-icon pack="fas" class="flex text-blue" icon="folder"></b-icon>
                <b class="ml-1">{{ $t("total") || 'Total' }}</b>: {{ models.length }}
              </template>

              <template #empty>
                <div class="has-text-centered">
                  {{ $t('no_results') || 'No hay resultados' }}
                </div>
              </template>
            </b-table>
          </section>
        </div>
      </div>
    </div>

    <div v-else class="panel ifc-panel">
      <div class="panel-heading ifc-panel-heading">
        <div class="flex items-center justify-between w-full gap-4 flex-wrap">
          <div class="min-w-0">
            <div class="flex items-center text-gray-800">
              <b-icon pack="fas" icon="layer-group" class="mr-2" />
              <span class="font-semibold text-xl">
                {{ $t('federated_models') || 'Modelos Federados' }}
              </span>
            </div>

            <p class="text-xs text-gray-500 mt-1">
              {{ federatedGroups.length }} {{ $t('groups') || 'grupos' }}
              ·
              {{ federatedReadyGroupsCount }} {{ $t('ready') || 'Completos' }}
            </p>
          </div>

          <button class="button is-primary is-small" @click="$refs.uploadFederatedModal.show()">
            <b-icon pack="fas" icon="plus" size="is-small" />&nbsp;
            {{ $t('add_federated_ifc_model') }}
          </button>
        </div>
      </div>

      <div v-if="isLoadingFederated" class="my-4 text-center">
        <scale-loader></scale-loader>
      </div>

      <div v-else>
        <b-table icon-pack="fas" :data="federatedGroups" :bordered="true">
          <template>
            <b-table-column field="federation_name" cell-class="vertical-center" sortable>
              <template v-slot:header>
                {{ $t('federated_group')}}
              </template>

              <template v-slot="props">
                <div class="flex items-center min-w-0">
                  <b-icon pack="fas" icon="layer-group" class="text-blue mr-2 flex-shrink-0" />
                  <div class="min-w-0">
                    <div class="truncate" :title="props.row.federation_name">
                      {{ props.row.federation_name }}
                    </div>
                  </div>
                </div>
              </template>
            </b-table-column>

            <b-table-column field="ifc_count" cell-class="vertical-center" width="220">
              <template v-slot:header>
                {{ $t('status') || 'Estado' }}
              </template>

              <template v-slot="props">
                <div class="flex items-center">
                  <b-icon
                    pack="fas"
                    :icon="allXktsReady(props.row) ? 'check-circle' : 'clock'"
                    :class="allXktsReady(props.row) ? 'text-green' : 'text-orange'"
                  />
                  <div class="ml-2">
                    <div>
                      {{ allXktsReady(props.row) ? $t('complete') : $t('pending') }}
                    </div>
                    <div class="text-xs text-gray-500">
                      {{ readyXktsCount(props.row) }} / {{ props.row.ifc_documents.length }} IFC
                    </div>
                  </div>
                </div>
              </template>
            </b-table-column>

            <b-table-column field="actions" cell-class="vertical-center" width="140">
              <template v-slot:header>
                {{ $t('actions') }}
              </template>

              <template v-slot="props">
                <div class="flex items-center justify-center gap-3 w-full h-full">
                  <b-tooltip :label="$t('add_ifc')" position="is-left" type="is-info">
                    <div @click="addToFederatedGroup(props.row)" class="flex cursor-pointer text-blue">
                      <b-icon pack="fas" icon="plus-circle" />
                    </div>
                  </b-tooltip>
                  <b-tooltip :label="$t('delete')" position="is-left" type="is-danger">
                    <div @click="removeFederatedGroup(props.row)" class="flex cursor-pointer text-red">
                      <b-icon pack="fas" icon="trash-alt" />
                    </div>
                  </b-tooltip>
                </div>
              </template>
            </b-table-column>
          </template>

          <template #empty>
            <div class="has-text-centered">
              {{ $t('no_results') || 'No hay resultados' }}
            </div>
          </template>
        </b-table>
      </div>
    </div>

    <upload-ifc-federated
      ref="uploadFederatedModal"
      @success="reloadFederated"
    />
  </div>
</template>

<script type="module">
import axios from 'axios';
import ScaleLoader from 'vue-spinner/src/ScaleLoader.vue'
import uploadIfcFederated from './upload-ifc-federated.vue'

export default {
  middleware: 'auth',

  components: {
    ScaleLoader,
    uploadIfcFederated,
  },

  props: {
    projectId: { type: Number, required: true },
  },

  data() {
    return {
      activeSection: 'individual',
      models: [],
      isLoading: false,
      federatedGroups: [],
      isLoadingFederated: false,
    }
  },

  computed: {
    individualReadyCount() {
      return this.models.filter(model => model.xktpath).length
    },

    federatedReadyGroupsCount() {
      return this.federatedGroups.filter(group => this.allXktsReady(group)).length
    },
  },

  mounted() {
    this.getIfcDocuments()
    this.getFederatedDocuments()
  },

  methods: {
    async getIfcDocuments() {
      this.isLoading = true
      const url = `/api/v2/project/${this.projectId}/ifc-documents`;

      try {
        const response = await axios.get(url);
        this.isLoading = false

        if (response.data && response.data.success) {
          this.models = (response.data.data || []).filter(d => !d.federation_id)
        } else {
          this.models = []
        }
      } catch (error) {
        this.isLoading = false
        console.log('error get ifc documents: ', error)
        this.models = []
      }
    },

    async removeIfc(model) {
      this.$dialog.confirm({
        title: this.$t("delete"),
        message: this.$t("delete_generic", [model.name]),
        confirmText: this.$t("delete"),
        type: "is-danger",
        hasIcon: true,
        onConfirm: async () => {
          this.isLoading = true;
          try {
            const url = `/api/v2/ifc-document/${model.id}/delete`;
            const { data } = await axios.post(url);

            if (data && data.success) {
              this.$toast.open({
                message: this.$t("success_deleting_generic"),
                type: "is-success",
                position: "is-top-right"
              });
              await this.sleep(800);
              this.reload();
            } else {
              this.isLoading = false;
              this.$toast.open({
                message: data.error || this.$t("error_saving"),
                type: "is-danger",
                position: "is-top-right"
              });
            }
          } catch (err) {
            this.isLoading = false;
            console.log('error deleting ifc document: ', err);
            this.$toast.open({
              message: this.$t("error_saving"),
              type: "is-danger",
              position: "is-top-right"
            });
          }
        }
      });
    },

    async getFederatedDocuments() {
      this.isLoadingFederated = true

      try {
        const resp = await axios.get(`/api/v2/project/${this.projectId}/ifc-federated-documents`)
        this.federatedGroups = (resp.data && resp.data.data) ? resp.data.data : []
      } catch (error) {
        console.log('error get federated documents: ', error)
        this.federatedGroups = []
      } finally {
        this.isLoadingFederated = false
      }
    },

    async removeFederatedGroup(group) {
      this.$dialog.confirm({
        title: this.$t("delete"),
        message: this.$t("delete_generic", [group.federation_name]),
        confirmText: this.$t("delete"),
        type: "is-danger",
        hasIcon: true,
        onConfirm: async () => {
          this.isLoadingFederated = true;
          try {
            const url = `/api/v2/project/${this.projectId}/ifc-federated-document/${group.federation_id}/delete`;
            const { data } = await axios.post(url);

            if (data && data.success) {
              this.$toast.open({
                message: this.$t("success_deleting_generic"),
                type: "is-success",
                position: "is-top-right"
              });
              await this.sleep(800);
              this.reloadFederated();
            } else {
              this.isLoadingFederated = false;
              this.$toast.open({
                message: data.error || this.$t("error_saving"),
                type: "is-danger",
                position: "is-top-right"
              });
            }
          } catch (err) {
            this.isLoadingFederated = false;
            console.log('error deleting federated group: ', err);
            this.$toast.open({
              message: this.$t("error_saving"),
              type: "is-danger",
              position: "is-top-right"
            });
          }
        }
      });
    },

    allXktsReady(group) {
      return group.ifc_documents.length > 0 &&
        group.ifc_documents.every(d => d.xktpath)
    },

    readyXktsCount(group) {
      return group.ifc_documents.filter(d => d.xktpath).length
    },

    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },

    reload() {
      this.getIfcDocuments()
    },

    addToFederatedGroup(group) {
      this.$refs.uploadFederatedModal.showForGroup(group.federation_id, group.federation_name, group.ifc_documents || [])
    },

    reloadFederated() {
      this.getFederatedDocuments()
    }
  },
};
</script>

<style scoped>
.ifc-panel {
  border: 1px solid #d8e2f1;
  border-radius: 12px;
  overflow: hidden;
  background: #ffffff;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.05);
}

.ifc-panel-heading {
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%) !important;
  border-bottom: 1px solid #e5e7eb;
  padding: 18px 20px;
}
</style>